/* eslint-disable no-alert */
/* eslint-disable no-console */
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import * as yup from 'yup'
import { useFormik } from 'formik'
import { Theme } from '@mui/material/styles'
import makeStyles from '@mui/styles/makeStyles'
import createStyles from '@mui/styles/createStyles'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Button from '@mui/material/Button'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { complement, isNil } from 'ramda'
import { CircularProgress, Backdrop } from '@mui/material'

import noop from 'src/utilities/noop'
import useSession from 'src/authentication/hooks/use-session'
import axios, { pluckData } from 'src/utilities/http-client'

let nonUsaStateValues = [
  'Australia',
  'Belgium',
  'Canada',
  'Czech Republic',
  'Germany',
  'Great Britain',
  'Iraq',
  'Ireland',
  'Mexico',
  'Netherlands',
  'New Zealand',
  'Qatar',
  'Saudi Arabia',
  'South Africa',
  'Scotland',
  'Slovakia',
  'Switzerland',
  'UAE',
]

let nonCapceLicenseTypes = [
  'Registered Nurse',
  'Advanced Care Paramedic (Canada Only)',
  'Primary Care Paramedic (Canada Only)',
  'EMT / Not Nationally Registered',
  'Paramedic / Not Nationally Registered',
  'Other / Not Nationally Registered',
  'Other',
]

let validText = /.*\S.*/

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    margin: {
      margin: theme.spacing(1),
    },
    withoutLabel: {
      marginTop: theme.spacing(3),
    },
    textField: {
      width: '48%',
    },
    textarea: {
      resize: 'both',
      width: '100%',
    },
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: '#fff',
    },
  }),
)

type SurveyInfo = {
  courseNumber: string
  hours: number
}

export default () => {
  let navigate = useNavigate()
  let session = useSession()
  let [loading, setLoading] = useState(false)
  let { quizID } = useParams()
  let [surveyInfo, setSurveyInfo] = useState<SurveyInfo>()

  let form = useFormik({
    validationSchema: yup.object({
      firstName: yup.string().required(),
      lastName: yup.string().required(),
      licenseNumber: yup.string().nullable(),
      licenseState: yup.string().required(),
      licenseType: yup.string().required(),
      licenseExpiration: yup.date().min('2000-01-01').nullable(),
      nremtCertificationNumber: yup.string().nullable(),
      nremtCertificationDate: yup.date().min('2000-01-01').nullable(),
      address: yup.string().required(),
      state: yup.string().required(),
      city: yup.string().required(),
      zip: yup.string().required(),
      primaryEmail: yup.string().required(),
      linkedServiceId: yup.string().nullable(),
      topicsThoroughAndInteresting: yup.number().required(),
      presenterGoodUnderstanding: yup.number().required(),
      videoQualityProfessional: yup.number().required(),
      benefitCareerGoals: yup.number().required(),
      knowledgePositiveImpactPractice: yup.number().required(),
      confusingTopics: yup.string().matches(validText).required(),
      thingsToBeContinued: yup.string().matches(validText).required(),
      thingsToBeImproved: yup.string().matches(validText).required(),
      explainImpactPractice: yup.string().matches(validText).required(),
    }),
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      primaryEmail: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      licenseState: '',
      licenseNumber: '',
      licenseExpiration: '',
      licenseType: '',
      nremtCertificationNumber: '',
      nremtCertificationDate: '',
      courseNumber: '',
      hours: 0,
      topicsThoroughAndInteresting: null,
      presenterGoodUnderstanding: null,
      videoQualityProfessional: null,
      benefitCareerGoals: null,
      knowledgePositiveImpactPractice: null,
      confusingTopics: null,
      thingsToBeContinued: null,
      thingsToBeImproved: null,
      explainImpactPractice: null,
      linkedServiceId: null,
    },
    // eslint-disable-next-line fp/no-nil
    onSubmit: (values) => {
      // eslint-disable-next-line fp/no-unused-expression
      setLoading(true)

      let expirationDates = {
        // eslint-disable-next-line fp/no-nil
        nremtCertificationDate:
          values.nremtCertificationDate === ''
            ? null
            : values.nremtCertificationDate,
        // eslint-disable-next-line fp/no-nil
        licenseExpiration:
          values.licenseExpiration === '' ? null : values.licenseExpiration,
      }

      axios
        .post('/api/v2/live-class/survey', {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          primaryEmail: values.primaryEmail,
          address: values.address,
          city: values.city,
          state: values.state,
          zip: values.zip,
          phone: values.phone,
          licenseState: values.licenseState,
          licenseNumber: values.licenseNumber,
          licenseType: values.licenseType,
          nremtCertificationNumber: values.nremtCertificationNumber,
          courseNumber: surveyInfo?.courseNumber,
          hours: surveyInfo?.hours,
          ...expirationDates,
        })
        .then(({ data }) => data)
        .then((data) => {
          axios.post('/api/v2/classroom/questionnaire', {
            SurveyID: data,
            CourseNumber: surveyInfo?.courseNumber,
            TopicsThoroughAndInteresting: values.topicsThoroughAndInteresting,
            PresenterGoodUnderstanding: values.presenterGoodUnderstanding,
            VideoQualityProfessional: values.videoQualityProfessional,
            BenefitCareerGoals: values.benefitCareerGoals,
            KnowledgePositiveImpactPractice:
              values.knowledgePositiveImpactPractice,
            ConfusingTopics: values.confusingTopics,
            ThingsToBeContinued: values.thingsToBeContinued,
            ThingsToBeImproved: values.thingsToBeImproved,
            ExplainImpactPractice: values.explainImpactPractice,
          })
          return data
        })
        .then((data) => {
          navigate(`/live/certificate/${data}`)
        })
    },
  })
  let classes = useStyles()

  useEffect(() => {
    if (quizID) {
      axios
        .get(`/api/v2/live-class/survey/info?quizID=${quizID}`)
        .then(pluckData)
        .then(setSurveyInfo)
    }
  }, [quizID])

  // eslint-disable-next-line fp/no-unused-expression,fp/no-nil
  useEffect(() => {
    if (session?.emailAddress) {
      // eslint-disable-next-line fp/no-unused-expression
      axios
        .get('/api/v2/classroom/user')
        .then(({ data }) =>
          Object.entries(data).forEach(([k, v]) =>
            k in form.values && complement(isNil)(v)
              ? form.setFieldValue(k, v)
              : noop,
          ),
        )
    }
  }, [session])

  return (
    <div>
      <form onSubmit={form.handleSubmit}>
        <div className="flex flex-row flex-wrap">
          <TextField
            variant="standard"
            className={`${classes.margin} ${classes.textField}`}
            label="First Name"
            name="firstName"
            value={form.values.firstName}
            onChange={form.handleChange}
            error={form.touched.firstName && !!form.errors.firstName}
            helperText={form.touched.firstName && form.errors.firstName}
          />

          <TextField
            variant="standard"
            className={`${classes.margin} ${classes.textField}`}
            label="Last Name"
            name="lastName"
            value={form.values.lastName}
            onChange={form.handleChange}
            error={form.touched.lastName && !!form.errors.lastName}
            helperText={form.touched.lastName && form.errors.lastName}
          />

          <TextField
            variant="standard"
            className={`${classes.margin} ${classes.textField}`}
            name="primaryEmail"
            type="email"
            label="Email Address"
            value={form.values.primaryEmail}
            onChange={form.handleChange}
            error={form.touched.primaryEmail && !!form.errors.primaryEmail}
            helperText={form.touched.primaryEmail && form.errors.primaryEmail}
          />

          <TextField
            variant="standard"
            className={`${classes.margin} ${classes.textField}`}
            label="Address"
            name="address"
            value={form.values.address}
            onChange={form.handleChange}
            error={form.touched.address && !!form.errors.address}
            helperText={form.touched.address && form.errors.address}
          />

          <TextField
            variant="standard"
            className={`${classes.margin} ${classes.textField}`}
            name="city"
            label="City"
            value={form.values.city}
            onChange={form.handleChange}
            error={form.touched.city && !!form.errors.city}
            helperText={form.touched.city && form.errors.city}
          />

          <FormControl
            variant="standard"
            className={`${classes.margin} ${classes.textField}`}
          >
            <InputLabel htmlFor="state">State</InputLabel>
            <Select
              value={form.values.state}
              onChange={form.handleChange('state') as any}
              error={form.touched.state && !!form.errors.state}
            >
              <MenuItem value="">State</MenuItem>
              <MenuItem value="Alaska">Alaska</MenuItem>
              <MenuItem value="Alabama">Alabama</MenuItem>
              <MenuItem value="Antigua and Barbuda">
                Antigua and Barbuda
              </MenuItem>
              <MenuItem value="Arkansas">Arkansas</MenuItem>
              <MenuItem value="Australia">Australia</MenuItem>
              <MenuItem value="Arizona">Arizona</MenuItem>
              <MenuItem value="The Bahamas">The Bahamas</MenuItem>
              <MenuItem value="Belgium">Belgium</MenuItem>
              <MenuItem value="California">California</MenuItem>
              <MenuItem value="Canada">Canada</MenuItem>
              <MenuItem value="Colorado">Colorado</MenuItem>
              <MenuItem value="Connecticut">Connecticut</MenuItem>
              <MenuItem value="Czech Republic">Czech Republic</MenuItem>
              <MenuItem value="Delaware">Delaware</MenuItem>
              <MenuItem value="DHS">DHS</MenuItem>
              <MenuItem value="District of Columbia">
                District of Columbia
              </MenuItem>
              <MenuItem value="Ecuador">Ecuador</MenuItem>
              <MenuItem value="Florida">Florida</MenuItem>
              <MenuItem value="France">France</MenuItem>
              <MenuItem value="Georgia">Georgia</MenuItem>
              <MenuItem value="Germany">Germany</MenuItem>
              <MenuItem value="Great Britain">Great Britain</MenuItem>
              <MenuItem value="Guam">Guam</MenuItem>
              <MenuItem value="Hawaii">Hawaii</MenuItem>
              <MenuItem value="Iowa">Iowa</MenuItem>
              <MenuItem value="Idaho">Idaho</MenuItem>
              <MenuItem value="Illinois">Illinois</MenuItem>
              <MenuItem value="Indiana">Indiana</MenuItem>
              <MenuItem value="Iraq">Iraq</MenuItem>
              <MenuItem value="Ireland">Ireland</MenuItem>
              <MenuItem value="Kansas">Kansas</MenuItem>
              <MenuItem value="Kentucky">Kentucky</MenuItem>
              <MenuItem value="Louisiana">Louisiana</MenuItem>
              <MenuItem value="Liechtenstein">Liechtenstein</MenuItem>
              <MenuItem value="Massachusetts">Massachusetts</MenuItem>
              <MenuItem value="Maryland">Maryland</MenuItem>
              <MenuItem value="Maine">Maine</MenuItem>
              <MenuItem value="Mexico">Mexico</MenuItem>
              <MenuItem value="Michigan">Michigan</MenuItem>
              <MenuItem value="Minnesota">Minnesota</MenuItem>
              <MenuItem value="Missouri">Missouri</MenuItem>
              <MenuItem value="Mississippi">Mississippi</MenuItem>
              <MenuItem value="Montana">Montana</MenuItem>
              <MenuItem value="North Carolina">North Carolina</MenuItem>
              <MenuItem value="North Dakota">North Dakota</MenuItem>
              <MenuItem value="Nebraska">Nebraska</MenuItem>
              <MenuItem value="Netherlands">Netherlands</MenuItem>
              <MenuItem value="New Zealand">New Zealand</MenuItem>
              <MenuItem value="New Hampshire">New Hampshire</MenuItem>
              <MenuItem value="New Jersey">New Jersey</MenuItem>
              <MenuItem value="New Mexico">New Mexico</MenuItem>
              <MenuItem value="Nevada">Nevada</MenuItem>
              <MenuItem value="New York">New York</MenuItem>
              <MenuItem value="Ohio">Ohio</MenuItem>
              <MenuItem value="Oklahoma">Oklahoma</MenuItem>
              <MenuItem value="Oregon">Oregon</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
              <MenuItem value="Pennsylvania">Pennsylvania</MenuItem>
              <MenuItem value="Philippines">Philippines</MenuItem>
              <MenuItem value="Puerto Rico">Puerto Rico</MenuItem>
              <MenuItem value="Poland">Poland</MenuItem>
              <MenuItem value="Qatar">Qatar</MenuItem>
              <MenuItem value="Rhode Island">Rhode Island</MenuItem>
              <MenuItem value="Saudi Arabia">Saudi Arabia</MenuItem>
              <MenuItem value="Serbia">Serbia</MenuItem>
              <MenuItem value="South Africa">South Africa</MenuItem>
              <MenuItem value="South Carolina">South Carolina</MenuItem>
              <MenuItem value="Scotland">Scotland</MenuItem>
              <MenuItem value="South Dakota">South Dakota</MenuItem>
              <MenuItem value="Slovakia">Slovakia</MenuItem>
              <MenuItem value="Switzerland">Switzerland</MenuItem>
              <MenuItem value="Tennessee">Tennessee</MenuItem>
              <MenuItem value="Texas">Texas</MenuItem>
              <MenuItem value="Trinidad and Tobago">
                Trinidad and Tobago
              </MenuItem>
              <MenuItem value="UAE">UAE</MenuItem>
              <MenuItem value="Utah">Utah</MenuItem>
              <MenuItem value="Virgin Islands">Virgin Islands</MenuItem>
              <MenuItem value="Virginia">Virginia</MenuItem>
              <MenuItem value="Vermont">Vermont</MenuItem>
              <MenuItem value="Washington">Washington</MenuItem>
              <MenuItem value="Wisconsin">Wisconsin</MenuItem>
              <MenuItem value="West Virginia">West Virginia</MenuItem>
              <MenuItem value="Wyoming">Wyoming</MenuItem>
            </Select>
          </FormControl>

          <TextField
            variant="standard"
            className={`${classes.margin} ${classes.textField}`}
            name="zip"
            label="Zip"
            value={form.values.zip}
            onChange={form.handleChange}
            error={form.touched.zip && !!form.errors.zip}
            helperText={form.touched.zip && form.errors.zip}
          />

          <TextField
            variant="standard"
            className={`${classes.margin} ${classes.textField}`}
            type="tel"
            label="Phone Number"
            name="phone"
            value={form.values.phone}
            onChange={form.handleChange}
          />

          <FormControl
            variant="standard"
            className={`${classes.margin} ${classes.textField}`}
          >
            <InputLabel htmlFor="licenseState">
              What state is your license held in?
            </InputLabel>
            <Select
              value={form.values.licenseState}
              onChange={form.handleChange('licenseState') as any}
              error={form.touched.licenseState && !!form.errors.licenseState}
            >
              <MenuItem value="">State</MenuItem>
              <MenuItem value="Alaska">Alaska</MenuItem>
              <MenuItem value="Alabama">Alabama</MenuItem>
              <MenuItem value="Antigua and Barbuda">
                Antigua and Barbuda
              </MenuItem>
              <MenuItem value="Arkansas">Arkansas</MenuItem>
              <MenuItem value="Australia">Australia</MenuItem>
              <MenuItem value="Arizona">Arizona</MenuItem>
              <MenuItem value="The Bahamas">The Bahamas</MenuItem>
              <MenuItem value="Belgium">Belgium</MenuItem>
              <MenuItem value="California">California</MenuItem>
              <MenuItem value="Canada">Canada</MenuItem>
              <MenuItem value="Colorado">Colorado</MenuItem>
              <MenuItem value="Connecticut">Connecticut</MenuItem>
              <MenuItem value="Czech Republic">Czech Republic</MenuItem>
              <MenuItem value="Delaware">Delaware</MenuItem>
              <MenuItem value="DHS">DHS</MenuItem>
              <MenuItem value="District of Columbia">
                District of Columbia
              </MenuItem>
              <MenuItem value="Ecuador">Ecuador</MenuItem>
              <MenuItem value="Florida">Florida</MenuItem>
              <MenuItem value="France">France</MenuItem>
              <MenuItem value="Georgia">Georgia</MenuItem>
              <MenuItem value="Germany">Germany</MenuItem>
              <MenuItem value="Great Britain">Great Britain</MenuItem>
              <MenuItem value="Guam">Guam</MenuItem>
              <MenuItem value="Hawaii">Hawaii</MenuItem>
              <MenuItem value="Iowa">Iowa</MenuItem>
              <MenuItem value="Idaho">Idaho</MenuItem>
              <MenuItem value="Illinois">Illinois</MenuItem>
              <MenuItem value="Indiana">Indiana</MenuItem>
              <MenuItem value="Iraq">Iraq</MenuItem>
              <MenuItem value="Ireland">Ireland</MenuItem>
              <MenuItem value="Kansas">Kansas</MenuItem>
              <MenuItem value="Kentucky">Kentucky</MenuItem>
              <MenuItem value="Louisiana">Louisiana</MenuItem>
              <MenuItem value="Liechtenstein">Liechtenstein</MenuItem>
              <MenuItem value="Massachusetts">Massachusetts</MenuItem>
              <MenuItem value="Maryland">Maryland</MenuItem>
              <MenuItem value="Maine">Maine</MenuItem>
              <MenuItem value="Mexico">Mexico</MenuItem>
              <MenuItem value="Michigan">Michigan</MenuItem>
              <MenuItem value="Minnesota">Minnesota</MenuItem>
              <MenuItem value="Missouri">Missouri</MenuItem>
              <MenuItem value="Mississippi">Mississippi</MenuItem>
              <MenuItem value="Montana">Montana</MenuItem>
              <MenuItem value="North Carolina">North Carolina</MenuItem>
              <MenuItem value="North Dakota">North Dakota</MenuItem>
              <MenuItem value="Nebraska">Nebraska</MenuItem>
              <MenuItem value="Netherlands">Netherlands</MenuItem>
              <MenuItem value="New Zealand">New Zealand</MenuItem>
              <MenuItem value="New Hampshire">New Hampshire</MenuItem>
              <MenuItem value="New Jersey">New Jersey</MenuItem>
              <MenuItem value="New Mexico">New Mexico</MenuItem>
              <MenuItem value="Nevada">Nevada</MenuItem>
              <MenuItem value="New York">New York</MenuItem>
              <MenuItem value="Ohio">Ohio</MenuItem>
              <MenuItem value="Oklahoma">Oklahoma</MenuItem>
              <MenuItem value="Oregon">Oregon</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
              <MenuItem value="Pennsylvania">Pennsylvania</MenuItem>
              <MenuItem value="Philippines">Philippines</MenuItem>
              <MenuItem value="Puerto Rico">Puerto Rico</MenuItem>
              <MenuItem value="Poland">Poland</MenuItem>
              <MenuItem value="Qatar">Qatar</MenuItem>
              <MenuItem value="Rhode Island">Rhode Island</MenuItem>
              <MenuItem value="Saudi Arabia">Saudi Arabia</MenuItem>
              <MenuItem value="Serbia">Serbia</MenuItem>
              <MenuItem value="South Africa">South Africa</MenuItem>
              <MenuItem value="South Carolina">South Carolina</MenuItem>
              <MenuItem value="Scotland">Scotland</MenuItem>
              <MenuItem value="South Dakota">South Dakota</MenuItem>
              <MenuItem value="Slovakia">Slovakia</MenuItem>
              <MenuItem value="Switzerland">Switzerland</MenuItem>
              <MenuItem value="Tennessee">Tennessee</MenuItem>
              <MenuItem value="Texas">Texas</MenuItem>
              <MenuItem value="Trinidad and Tobago">
                Trinidad and Tobago
              </MenuItem>
              <MenuItem value="UAE">UAE</MenuItem>
              <MenuItem value="Utah">Utah</MenuItem>
              <MenuItem value="Virgin Islands">Virgin Islands</MenuItem>
              <MenuItem value="Virginia">Virginia</MenuItem>
              <MenuItem value="Vermont">Vermont</MenuItem>
              <MenuItem value="Washington">Washington</MenuItem>
              <MenuItem value="Wisconsin">Wisconsin</MenuItem>
              <MenuItem value="West Virginia">West Virginia</MenuItem>
              <MenuItem value="Wyoming">Wyoming</MenuItem>
            </Select>
          </FormControl>

          <FormControl
            variant="standard"
            className={`${classes.margin} ${classes.textField}`}
          >
            <InputLabel htmlFor="state">License Type</InputLabel>
            <Select
              value={form.values.licenseType}
              onChange={form.handleChange('licenseType') as any}
              error={form.touched.licenseType && !!form.errors.licenseType}
              defaultValue=""
            >
              <MenuItem value="Paramedic">Paramedic</MenuItem>
              <MenuItem value="Registered Nurse">Registered Nurse</MenuItem>
              <MenuItem value="CFR">Certified First Responder</MenuItem>
              <MenuItem value="EMR">Emergency Medical Responder</MenuItem>
              <MenuItem value="EMT">EMT</MenuItem>
              <MenuItem value="EMT-1">EMT-1</MenuItem>
              <MenuItem value="EMT-B">EMT-B</MenuItem>
              <MenuItem value="EMT-D">EMT-D</MenuItem>
              <MenuItem value="AEMT">AEMT</MenuItem>
              <MenuItem value="EMT-2">EMT-2</MenuItem>
              <MenuItem value="EMT-CC">EMT-CC</MenuItem>
              <MenuItem value="Advanced Care Paramedic (Canada Only)">
                Advanced Care Paramedic (Canada Only)
              </MenuItem>
              <MenuItem value="Primary Care Paramedic (Canada Only)">
                Primary Care Paramedic (Canada Only)
              </MenuItem>
              <MenuItem value="Physician">Physician</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          <TextField
            variant="standard"
            className={`${classes.margin} ${classes.textField}`}
            value={form.values.licenseNumber}
            label="License Number"
            name="licenseNumber"
            onChange={form.handleChange}
            error={form.touched.licenseNumber && !!form.errors.licenseNumber}
            helperText={form.touched.licenseNumber && form.errors.licenseNumber}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="License Expiration Date"
              value={dayjs(form.values.licenseExpiration)}
              onChange={(dayJsVal: any) =>
                // every other MUI component that I can think of sends an event, not this one
                form.handleChange({
                  target: {
                    name: 'licenseExpiration',
                    value: dayjs(dayJsVal).format('MM/DD/YYYY'),
                  },
                })
              }
            />
          </LocalizationProvider>

          <TextField
            variant="standard"
            className={`${classes.margin} ${classes.textField}`}
            error={
              form.touched.nremtCertificationNumber &&
              !!form.errors.nremtCertificationNumber
            }
            helperText={
              form.touched.nremtCertificationNumber &&
              form.errors.nremtCertificationNumber
            }
            label="NREMT Certification Number (This should not be your EMS ID)"
            value={form.values.nremtCertificationNumber}
            name="nremtCertificationNumber"
            onChange={form.handleChange}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="NREMT Expiration Date"
              value={dayjs(form?.values?.nremtCertificationDate)}
              onChange={(dayJsVal: any) =>
                // every other MUI component that I can think of sends an event, not this one
                form.handleChange({
                  target: {
                    name: 'nremtCertificationDate',
                    value: dayjs(dayJsVal).format('MM/DD/YYYY'),
                  },
                })
              }
            />
          </LocalizationProvider>
        </div>
        <Divider className="mt-4" />

        <div className="my-3">
          <Typography>
            For the next six questions please rate your response from a scale of
            1 to 5 with 1 being worst and 5 being best.
          </Typography>

          <FormControl
            error={
              form.touched.topicsThoroughAndInteresting &&
              !!form.errors.topicsThoroughAndInteresting
            }
            component="fieldset"
            className="w-full"
          >
            <FormLabel component="legend">
              Were the topics presented in thorough and interesting manner?
            </FormLabel>
            <RadioGroup
              aria-label="topicsThoroughAndInteresting"
              name="topicsThoroughAndInteresting"
              onChange={form.handleChange}
              className="flex-row"
            >
              <FormControlLabel value="1" control={<Radio />} label="1" />
              <FormControlLabel value="2" control={<Radio />} label="2" />
              <FormControlLabel value="3" control={<Radio />} label="3" />
              <FormControlLabel value="4" control={<Radio />} label="4" />
              <FormControlLabel value="5" control={<Radio />} label="5" />
            </RadioGroup>
          </FormControl>

          <FormControl
            error={
              form.touched.presenterGoodUnderstanding &&
              !!form.errors.presenterGoodUnderstanding
            }
            component="fieldset"
            className="w-full "
          >
            <FormLabel component="legend">
              The presenter had a good understanding of the content being
              delivered?
            </FormLabel>
            <RadioGroup
              aria-label="presenterGoodUnderstanding"
              name="presenterGoodUnderstanding"
              onChange={form.handleChange}
              className="flex-row"
            >
              <FormControlLabel value="1" control={<Radio />} label="1" />
              <FormControlLabel value="2" control={<Radio />} label="2" />
              <FormControlLabel value="3" control={<Radio />} label="3" />
              <FormControlLabel value="4" control={<Radio />} label="4" />
              <FormControlLabel value="5" control={<Radio />} label="5" />
            </RadioGroup>
          </FormControl>

          <FormControl
            error={
              form.touched.videoQualityProfessional &&
              !!form.errors.videoQualityProfessional
            }
            component="fieldset"
            className="w-full"
          >
            <FormLabel component="legend">
              The video quality was professional?
            </FormLabel>
            <RadioGroup
              aria-label="videoQualityProfessional"
              name="videoQualityProfessional"
              onChange={form.handleChange}
              className="flex-row"
            >
              <FormControlLabel value="1" control={<Radio />} label="1" />
              <FormControlLabel value="2" control={<Radio />} label="2" />
              <FormControlLabel value="3" control={<Radio />} label="3" />
              <FormControlLabel value="4" control={<Radio />} label="4" />
              <FormControlLabel value="5" control={<Radio />} label="5" />
            </RadioGroup>
          </FormControl>

          <FormControl
            error={
              form.touched.benefitCareerGoals &&
              !!form.errors.benefitCareerGoals
            }
            component="fieldset"
            className="w-full"
          >
            <FormLabel component="legend">
              The materials presented will benefit me in my career goals
            </FormLabel>
            <RadioGroup
              aria-label="benefitCareerGoals"
              name="benefitCareerGoals"
              onChange={form.handleChange}
              className="flex-row"
            >
              <FormControlLabel value="1" control={<Radio />} label="1" />
              <FormControlLabel value="2" control={<Radio />} label="2" />
              <FormControlLabel value="3" control={<Radio />} label="3" />
              <FormControlLabel value="4" control={<Radio />} label="4" />
              <FormControlLabel value="5" control={<Radio />} label="5" />
            </RadioGroup>
          </FormControl>

          <FormControl
            error={
              form.touched.knowledgePositiveImpactPractice &&
              !!form.errors.knowledgePositiveImpactPractice
            }
            component="fieldset"
            className="w-full"
          >
            <FormLabel component="legend">
              How strongly do you believe the knowledge gained from this
              educational activity will positively impact your practice?
            </FormLabel>
            <RadioGroup
              aria-label="knowledgePositiveImpactPractice"
              name="knowledgePositiveImpactPractice"
              onChange={form.handleChange}
              className="flex-row"
            >
              <FormControlLabel value="1" control={<Radio />} label="1" />
              <FormControlLabel value="2" control={<Radio />} label="2" />
              <FormControlLabel value="3" control={<Radio />} label="3" />
              <FormControlLabel value="4" control={<Radio />} label="4" />
              <FormControlLabel value="5" control={<Radio />} label="5" />
            </RadioGroup>
          </FormControl>

          <TextField
            name="confusingTopics"
            error={
              form.touched.confusingTopics && !!form.errors.confusingTopics
            }
            label="Are there any topics that still confuse you?"
            multiline
            variant="outlined"
            className="w-full my-3"
            inputProps={{ className: classes.textarea }}
            onChange={form.handleChange}
          />

          <TextField
            name="thingsToBeContinued"
            error={
              form.touched.thingsToBeContinued &&
              !!form.errors.thingsToBeContinued
            }
            label="Likes/strengths/things to be continued?"
            multiline
            variant="outlined"
            className="w-full my-3"
            inputProps={{ className: classes.textarea }}
            onChange={form.handleChange}
          />

          <TextField
            name="thingsToBeImproved"
            error={
              form.touched.thingsToBeImproved &&
              !!form.errors.thingsToBeImproved
            }
            label="Dislikes/weaknesses/things to be changed or improved?"
            multiline
            variant="outlined"
            className="w-full my-3"
            inputProps={{ className: classes.textarea }}
            onChange={form.handleChange}
          />

          <TextField
            name="explainImpactPractice"
            error={
              form.touched.explainImpactPractice &&
              !!form.errors.explainImpactPractice
            }
            label="Discuss three things you learned during this educational activity and explain how at least one of them will impact your future practice."
            multiline
            variant="outlined"
            className="w-full my-3"
            inputProps={{ className: classes.textarea }}
            onChange={form.handleChange}
          />
        </div>

        <div className="flex flex-row flex-wrap" />
        <div className="flex justify-end">
          {!nonCapceLicenseTypes.includes(form.values.licenseType) &&
            !nonUsaStateValues.includes(form.values.licenseState) &&
            (form.values.nremtCertificationDate === '' ||
              !!form.errors.nremtCertificationDate ||
              !dayjs(form.values.nremtCertificationDate).isValid() ||
              form.values.licenseExpiration === '' ||
              !!form.errors.licenseExpiration ||
              !dayjs(form.values.licenseExpiration).isValid()) && (
              <Typography className="my-3">
                Looks like there is some incorrect or missing information,
                please double check the form above, specifically your license
                and NREMT expiration.
                <br />
                If you are not NREMT registered please change your license type
                to reflect that.
              </Typography>
            )}
        </div>
        <div className="flex justify-end">
          <Button
            variant="contained"
            color="primary"
            type="submit"
            className="mt-4 mr-3"
          >
            Submit
          </Button>
        </div>
      </form>
      <div>
        <Backdrop className={classes.backdrop} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    </div>
  )
}
